import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  NgZone,
  OnDestroy,
  OnInit,
  signal,
  untracked,
  ViewChild,
} from '@angular/core'
import { MapComponent } from '../../common/index'
import { RetailComponent } from 'app/mobility/retail/retail.component'
import { AuthenticationService, ContextService } from 'app/services'
import { DomSanitizer, Title } from '@angular/platform-browser'
import { MobilityService } from 'app/services/mobility.service'
import { IsolinesService } from 'app/services/isolines.service'
import { TranslateService } from '@ngx-translate/core'
import { HttpClient } from '@angular/common/http'
import { combineLatest, filter, firstValueFrom } from 'rxjs'
import { toObservable } from '@angular/core/rxjs-interop'
import { environment } from '../../../environments/environment'
import OOHClusterLayer from './OOHClusterLayer'
import {
  type PoiRow,
  type PoisFilters,
  injectPoisQuery,
} from './model/pois.model'
import { injectMetadataQuery } from './model/metadata.model'
import { injectTagsQuery } from './model/tags.model'
import { injectStreetsQuery, StreetsFilters } from './model/streets.model'
import { injectTrafficQuantilesQuery } from './model/traffic-quantiles.model'
import {
  injectAggregationsQuery,
  type AggregationsFilters,
} from './model/aggregations.model'
import {
  injectPoiInfoQuery,
  injectPoiCompareDeliveryQuery,
  type PoiInfoFilters,
  type PoiCompareDeliveryFilters,
  type PoiDetailRow,
} from './model/poi-info.model'
import {
  injectCompareDeliveriesQuery,
  type CompareDeliveriesFilters,
} from './model/compare-deliveries.model'
import {
  injectCompareAggregationsQuery,
  type CompareAggregationsFilters,
} from '../campaigns/models/compare-aggregations.model'
import { injectCartoClient } from './services/carto.helper'
import { Delivery, Vertical } from '../../models/Vertical'
import { Colors } from 'app/common/config/constants'
import * as OohStatsHelper from './helpers/ooh-stats.helper'
import * as OohMapHelper from './helpers/ooh-map.helper'
import { groupTags } from './helpers/tags.helper'

@Component({
  standalone: false,
  selector: 'app-ooh',
  templateUrl: './ooh.component.html',
  styleUrls: [
    './../mobility/mobility.component.scss',
    './../retail/retail.component.scss',
    './ooh.component.scss',
  ],
})
export class OohComponent extends RetailComponent implements OnInit, OnDestroy {
  @ViewChild('map', { static: false }) map: MapComponent

  highlightDst = 7
  floatBarCollapsed = false

  clusterPointsId = 'ooh-cluster-points'
  clusterLabelsId = 'ooh-cluster-labels'
  trafficBaseId = 'ooh-traffic-tileset-base'
  trafficDataId = 'ooh-traffic-tileset-data'

  campaignCreationActive = false
  campaignPOIIds: string[] = [] // ids of the pois to be retrieved from `this.allPois`
  showCampaignCreationModal = false

  tagLabels: Record<string, string> = {}
  tagIds: Record<string, number> = {}

  clusterLayer = null
  _aggregatedStatsLoading = false

  poisFilters = signal<PoisFilters>({
    delivery_id: 0,
    delivery_start_date: '',
    filter_combined: 'All-All',
    tag_groups: [],
  })
  poisQuery = injectPoisQuery(this.poisFilters)

  metadataQuery = injectMetadataQuery()
  tagsQuery = injectTagsQuery()

  streetsFilters = signal<StreetsFilters>({
    delivery_id: 0,
    filter_combined: 'All-All',
  })
  streetsQuery = injectStreetsQuery(this.streetsFilters)
  private streetsData$ = toObservable(this.streetsQuery.data)
  private poisData$ = toObservable(this.poisQuery.data)

  trafficQuantilesQuery = injectTrafficQuantilesQuery(this.poisFilters)
  private trafficQuantilesData$ = toObservable(this.trafficQuantilesQuery.data)

  aggregationsFilters = signal<AggregationsFilters>({
    delivery_id: 0,
    delivery_start_date: '',
    filter_combined: 'All-All',
    columns: [],
  })
  aggregationsQuery = injectAggregationsQuery(this.aggregationsFilters)

  poiInfoFilters = signal<PoiInfoFilters>({
    poi_id: '',
    delivery_id: 0,
    delivery_start_date: '',
    filter_combined: 'All-All',
  })
  poiInfoQuery = injectPoiInfoQuery(this.poiInfoFilters)

  comparePoiInfoFilters = signal<PoiInfoFilters>({
    poi_id: '',
    delivery_id: 0,
    delivery_start_date: '',
    filter_combined: 'All-All',
  })
  comparePoiInfoQuery = injectPoiInfoQuery(this.comparePoiInfoFilters)
  private comparePoiData$ = toObservable(this.comparePoiInfoQuery.data)
  private poiData$ = toObservable(this.poiInfoQuery.data)

  compareDeliveryFilters = signal<PoiCompareDeliveryFilters>({
    poi_id: '',
    delivery_id_1: 0,
    delivery_id_2: 0,
    delivery_start_date_1: '',
    delivery_start_date_2: '',
    filter_combined: 'All-All',
  })
  compareDeliveryQuery = injectPoiCompareDeliveryQuery(
    this.compareDeliveryFilters,
  )
  private compareDeliveryData$ = toObservable(this.compareDeliveryQuery.data)
  private hasDeliveryCompare = computed(
    () => !!this.compareDeliveryFilters().delivery_id_2,
  )

  compareDeliveriesFilters = signal<CompareDeliveriesFilters>({
    delivery_id_1: 0,
    delivery_start_date_1: '',
    delivery_id_2: 0,
    delivery_start_date_2: '',
    filter_combined: 'All-All',
    tag_groups: [],
  })
  compareDeliveriesQuery = injectCompareDeliveriesQuery(
    this.compareDeliveriesFilters,
  )
  private compareDeliveriesData$ = toObservable(
    this.compareDeliveriesQuery.data,
  )

  compareAggregationsFilters = signal<CompareAggregationsFilters>({
    delivery_id_1: 0,
    delivery_id_2: 0,
    delivery_start_date_1: '',
    delivery_start_date_2: '',
    filter_combined: 'All-All',
    columns: [],
  })
  compareAggregationsQuery = injectCompareAggregationsQuery(
    this.compareAggregationsFilters,
  )

  private cartoClient = injectCartoClient()

  colorMen = Colors.colorMen
  colorWomen = Colors.colorWomen

  get tagsList() {
    return groupTags(this.tagsQuery.data() ?? [])
  }

  // main check for 'politis features'
  get hasCampaignCreation() {
    return (
      this.currentDelivery && this.currentDelivery.config.hasCampaignCreation
    )
  }

  get hasTrafficData() {
    return this.currentDelivery && this.currentDelivery.config.hasTrafficData
  }

  get hasAllPOIsInPOIMap() {
    return (
      this.currentDelivery && this.currentDelivery.config.hasAllPOIsInPOIMap
    )
  }

  get summaryName() {
    return this.hasCampaignCreation
      ? this.createDeliveryLabel(this.currentDelivery)
      : this.currentDelivery.name
  }

  get clusterConfig() {
    const columns = {
      [this.keyIndicator]: {
        aggregate_function: 'sum',
        aggregated_column: this.keyIndicator,
      },
      [this.keyIndicator + '_avg']: {
        aggregate_function: 'avg',
        aggregated_column: this.keyIndicator,
      },
      region_id: {
        aggregate_function: 'mode',
        aggregated_column: 'region_id',
      },
      id: {
        aggregate_function: 'min',
        aggregated_column: 'id',
      },
    }
    if (this.compareOptions) {
      columns[this.keyIndicator + '_other'] = {
        aggregate_function: 'sum',
        aggregated_column: this.keyIndicator + '_other',
      }
    }
    return {
      placement: 'centroid',
      resolution: 64,
      threshold: 1,
      columns,
    }
  }

  private isValidStat = OohStatsHelper.isValidStat
  private safePercent = OohStatsHelper.safePercent

  get globalAggsWidgetData() {
    const aggs = this.globalReferenceAggs

    if (!aggs) {
      return []
    }

    if (
      this.compareMode &&
      !this.hasCampaignCreation &&
      !this.selectedCompareDelivery
    ) {
      return []
    }
    const { sum, avg } = aggs
    if (!this.isValidStat(sum)) return []

    let percent = this.safePercent(sum, avg)
    let comparisonMode = false
    let compVal = null
    if (
      Array.isArray(this.mobilityStats.visitors) &&
      this.mobilityStats.visitors.length === 2
    ) {
      const refVal = this.mobilityStats.visitors[0]
      compVal = this.mobilityStats.visitors[1]
      percent = this.safePercent(refVal, compVal)
      comparisonMode = true
    }
    const out: any = [
      {
        icon: '/assets/img/icons/positive-clear/iso-walking.svg',
        label: this.translateService.instant(this.keyIndicatorLabel),
        value: Math.round(sum),
        percent,
      },
    ]
    if (!comparisonMode) {
      out.push({
        label: this.translateService.instant('AVERAGE'),
        value: Math.round(avg),
      })
    } else {
      out.push({
        label: this.translateService.instant('COMPARED'),
        value: Math.round(compVal),
      })
    }
    return out
  }

  get globalAggsWidgetTooltip() {
    if (this.comparePoiDetail) {
      return 'Reach value for the selected "reference" POI and deviation with the selected "compared" POI\'s reach value in the selected delivery'
    } else {
      return 'Reach value for the selected POI and deviation with its average reach for all the POIs in the selected delivery'
    }
  }

  get trafficWidgetData() {
    if (
      !this.mobilityStats ||
      !this.mobilityStats.poiTraffic ||
      !this.mobilityStats.globalTraffic
    ) {
      return []
    }

    const poiTraffic = this.mobilityStats.poiTraffic
    const avg = this.mobilityStats.globalTraffic.avg

    if (
      this.compareMode &&
      !this.hasCampaignCreation &&
      !this.selectedCompareDelivery
    ) {
      return []
    }

    let percent: number | null = null
    let sum = 0
    let comparisonMode = false
    let compVal = null
    if (Array.isArray(poiTraffic)) {
      sum = poiTraffic[0].sum || 0
      compVal = poiTraffic[1].sum || 0
      percent = this.safePercent(sum, compVal)
      comparisonMode = true
    } else {
      sum = poiTraffic.sum || 0
      percent = this.safePercent(sum, avg)
    }
    if (!this.isValidStat(sum)) return []

    const out: any = [
      {
        icon: '/assets/img/icons/positive-clear/iso-by-car.svg',
        label: this.translateService.instant('Vehicles Impacted'),
        value: Math.floor(sum),
        percent,
      },
    ]
    if (!comparisonMode) {
      out.push({
        label: this.translateService.instant('AVERAGE'),
        value: Math.round(avg),
      })
    } else {
      out.push({
        label: this.translateService.instant('COMPARED'),
        value: Math.round(compVal),
      })
    }
    return out
  }

  get trafficWidgetTooltip() {
    if (this.comparePoiDetail) {
      return 'Vehicles impacted for the selected "reference" POI and deviation with the selected "compared" POI\'s vehicles impacted value in the selected delivery'
    } else {
      return 'Vehicles impacted for the selected POI and deviation with its average for all the vehicle impacts in the selected delivery'
    }
  }

  get isPoiDetailLoading() {
    if (!this.selectedPOI) {
      return false
    }

    return (
      this.poiInfoQuery.isFetching() ||
      this.poiInfoQuery.isLoading() ||
      this._aggregatedStatsLoading
    )
  }

  get isPoisLoading() {
    return this.poisQuery.isFetching() || this.poisQuery.isLoading()
  }

  get isCompareLoading() {
    if (this.selectedCompareDelivery) {
      if (this.selectedPOI) {
        return (
          this.compareDeliveryQuery.isFetching() ||
          this.compareDeliveryQuery.isLoading()
        )
      }
      return (
        this.compareDeliveriesQuery.isFetching() ||
        this.compareDeliveriesQuery.isLoading() ||
        this.compareAggregationsQuery.isFetching() ||
        this.compareAggregationsQuery.isLoading()
      )
    }

    return (
      !!this.selectedComparePOI &&
      (this.comparePoiInfoQuery.isFetching() ||
        this.comparePoiInfoQuery.isLoading())
    )
  }

  get poiDetail(): PoiDetailRow | null {
    if (!this.selectedPOI) return null
    if (this.selectedCompareDelivery) {
      const data = this.compareDeliveryQuery.data()
      return data?.[0] ?? null
    }
    return this.poiInfoQuery.data() ?? null
  }

  get comparePoiDetail(): PoiDetailRow | null {
    if (!this.selectedPOI) return null
    // POI-to-POI comparison takes priority over delivery comparison
    if (this.selectedComparePOI) {
      return this.comparePoiInfoQuery.data() ?? null
    }
    if (this.selectedCompareDelivery) {
      const data = this.compareDeliveryQuery.data()
      return data?.[1] ?? null
    }
    return null
  }

  get poiReachWidgetData() {
    const d = this.poiDetail
    if (!d) return []
    const isFiltered = !this.context.isEntirePopulationActive()
    const columns = isFiltered
      ? OohStatsHelper.getColumnsFromTargetFilter(
          this.context.filters['target'],
        )
      : []
    const value = OohStatsHelper.nvisitorsFromColumns(d, columns)
    if (!this.isValidStat(value)) return []
    const comp = this.comparePoiDetail
    if (comp) {
      const compValue = OohStatsHelper.nvisitorsFromColumns(comp, columns)
      return [
        {
          icon: '/assets/img/icons/positive-clear/iso-walking.svg',
          label: this.translateService.instant(this.keyIndicatorLabel),
          value: Math.round(value),
          percent: this.safePercent(value, compValue),
        },
        {
          label: this.translateService.instant('COMPARED'),
          value: Math.round(compValue),
        },
      ]
    }
    const filteredAvg = isFiltered ? this.aggregationsQuery.data()?.avg : null
    const avg = filteredAvg ?? this.globalReferenceAggs?.avg ?? 0
    return [
      {
        icon: '/assets/img/icons/positive-clear/iso-walking.svg',
        label: this.translateService.instant(this.keyIndicatorLabel),
        value: Math.round(value),
        percent: this.safePercent(value, avg),
      },
      {
        label: this.translateService.instant('AVERAGE'),
        value: Math.round(avg),
      },
    ]
  }

  get poiTrafficWidgetData() {
    const d = this.poiDetail
    if (!d || !this.isValidStat(d.traffic)) return []
    const comp = this.comparePoiDetail
    if (comp) {
      return [
        {
          icon: '/assets/img/icons/positive-clear/iso-by-car.svg',
          label: this.translateService.instant('Vehicles Impacted'),
          value: Math.floor(d.traffic),
          percent: this.safePercent(d.traffic, comp.traffic),
        },
        {
          label: this.translateService.instant('COMPARED'),
          value: Math.round(comp.traffic),
        },
      ]
    }
    const avg = this.mobilityStats?.globalTraffic?.avg || 0
    return [
      {
        icon: '/assets/img/icons/positive-clear/iso-by-car.svg',
        label: this.translateService.instant('Vehicles Impacted'),
        value: Math.floor(d.traffic),
        percent: this.safePercent(d.traffic, avg),
      },
      {
        label: this.translateService.instant('AVERAGE'),
        value: Math.round(avg),
      },
    ]
  }

  get poiGenderData() {
    const d = this.poiDetail
    if (!d) return []
    const selection = this.genderSelection
    const ref = OohStatsHelper.genderDataFromDetail(d, selection)
    if (!ref) return []
    const comp = this.comparePoiDetail
    if (comp) {
      const compData = OohStatsHelper.genderDataFromDetail(comp, selection)
      return [ref, compData]
    }
    return ref
  }

  get poiAgeData() {
    const d = this.poiDetail
    if (!d) return []
    const selection = this.ageSelection
    const ref = OohStatsHelper.ageDataFromDetail(d, true, selection)
    if (!ref) return []
    const comp = this.comparePoiDetail
    if (comp) {
      const compData = OohStatsHelper.ageDataFromDetail(comp, true, selection)
      return [ref, compData]
    }
    return ref
  }

  override get compareLabels() {
    if (this.selectedComparePOI) {
      return [
        `Asset ${this.currentSelectValue}`,
        `Asset ${this.currentCompareValue}`,
      ]
    } else if (this.selectedCompareDelivery) {
      return [
        this.context.getContext().delivery.name,
        this.selectedCompareDelivery.name,
      ]
    }
    return []
  }

  get visitorProfileSelection(): string[] {
    return this.targetSubfilterSelection('visitor_profile')
  }

  get genderSelection(): string[] {
    return this.targetSubfilterSelection('gender')
  }

  get ageSelection(): string[] {
    return this.targetSubfilterSelection('age')
  }

  private targetSubfilterSelection(id: string): string[] {
    const target = this.context.filters['target']
    const sub = target?.subfilters?.find((s: any) => s.id === id)
    const val = sub?.value
    if (val === undefined || val === null) return []
    return Array.isArray(val) ? val : [val]
  }

  get screenReachStats() {
    const stats = this.mobilityStats?.visitors
    if (stats === undefined || stats === null) return stats
    const d = this.poiDetail
    if (!d) return stats
    if (this.context.isEntirePopulationActive()) return stats
    const columns = OohStatsHelper.getColumnsFromTargetFilter(
      this.context.filters['target'],
    )
    if (!columns.length) return stats
    const value = OohStatsHelper.nvisitorsFromColumns(d, columns)
    const comp = this.comparePoiDetail
    if (comp) {
      return [value, OohStatsHelper.nvisitorsFromColumns(comp, columns)]
    }
    return value
  }

  get poiVisitorProfileData() {
    const d = this.poiDetail
    if (!d) return []
    const selection = this.visitorProfileSelection
    const ref = OohStatsHelper.visitorProfileFromDetail(d, selection)
    if (!ref) return []
    const comp = this.comparePoiDetail
    if (comp) {
      const compData = OohStatsHelper.visitorProfileFromDetail(comp, selection)
      return [ref, compData]
    }
    return ref
  }

  _fetchAggregatedStats(poiId: string, delivery?: Delivery): Promise<any> {
    const baseFilters = this.poiInfoFilters()
    const filters = delivery
      ? {
          ...baseFilters,
          delivery_id: Number(delivery.id),
          delivery_start_date: delivery.start,
        }
      : baseFilters
    const day = this.context.filters['day']?.value || 'All'
    return OohStatsHelper.fetchAggregatedStats(
      this.cartoClient,
      filters,
      poiId,
      day,
    )
  }

  get globalReferenceAggs() {
    if (
      !this.mobilityStats ||
      !this.mobilityStats.globalAggs ||
      !this.mobilityStats.visitors
    ) {
      return null
    }

    const aggs = Array.isArray(this.mobilityStats.globalAggs)
      ? this.mobilityStats.globalAggs[0]
      : this.mobilityStats.globalAggs

    const sum = Array.isArray(this.mobilityStats.visitors)
      ? this.mobilityStats.visitors[0]
      : this.mobilityStats.visitors

    return { ...aggs, sum }
  }

  get tagsSelected() {
    return this.context.$currentTags.getValue()
  }

  get minMaxSelected() {
    return this.context.$minMaxFilter.getValue()
  }

  get legendTitle() {
    return this.hasTrafficData ? 'Traffic volume' : null
  }

  get legendSubtitle() {
    return this.hasTrafficData
      ? 'This legend qualitatively measures the traffic density on the streets represented on the map.'
      : null
  }

  get tooltipCompareMode() {
    const tooltipId = String(this.hoverPopupData.data.id)
    if (
      this.selectedComparePOI &&
      tooltipId !== String(this.selectedPOI) &&
      tooltipId !== String(this.selectedComparePOI)
    ) {
      return false
    }
    return this.compareMode || !!this.selectedComparePOI
  }

  get allPoisInCampaign() {
    return this.campaignPOIIds.length === 1 && this.campaignPOIIds[0] === 'all'
  }

  get poisForCreationModal() {
    return this.allPoisInCampaign
      ? this.allPois.map((p) => p.id)
      : this.campaignPOIIds
  }

  get politisLogos() {
    return environment.politis
  }

  @ViewChild('compareSelectfilter', { static: false }) compareSelectfilter

  constructor(
    protected authService: AuthenticationService,
    protected context: ContextService,
    protected sanitizer: DomSanitizer,
    protected changes: ChangeDetectorRef,
    protected mobilityService: MobilityService,
    protected isolinesService: IsolinesService,
    protected translateService: TranslateService,
    protected titleService: Title,
    protected http: HttpClient,
    protected zone: NgZone,
  ) {
    super(
      authService,
      context,
      sanitizer,
      changes,
      mobilityService,
      isolinesService,
      translateService,
      titleService,
      http,
      zone,
    )

    effect(() => {
      const orgs = this.metadataQuery.data()
      if (!orgs?.length) return

      const org = orgs[0]
      const verticalData = org.config.verticals[0]
      const parameters = org.config.parameters.default

      const delivery = new Delivery({ config: parameters })
      delivery.territory = {
        name: verticalData.territories[0],
        id: org.terrunit_region_id,
        bbox: [],
        region_id: org.terrunit_region_id,
        terrunit: 0,
      }

      const vertical = new Vertical({
        id: verticalData.id,
        slug: 'ooh',
        title: verticalData.title,
        territories: verticalData.territories,
      })
      vertical.delivery = delivery
      vertical.deliveries = {
        [verticalData.territories[0]]: [delivery],
      }

      this.context.organization = {
        id: org.org_id,
        name: org.name,
        terrunit_region_id: org.terrunit_region_id,
        config: org.config,
      }

      // Tag IDs/labels are now populated from the dev_tags named source
      // via the tagsQuery effect below

      this.context.resetContext(vertical)
      this.context.$currentVertical.next(vertical)
      this.context.$verticals.next([vertical])
      this.changes.detectChanges()
    })

    effect(() => {
      const tags = this.tagsQuery.data()

      if (!tags?.length) return

      for (const tag of tags) {
        this.tagIds[tag.name] = tag.id
        this.tagLabels[tag.name] = tag.label ?? tag.name
      }

      this.changes.detectChanges()
    })

    effect(() => {
      const rows = this.poisQuery.data()

      if (!rows) {
        return
      }

      // Compare-delivery mode owns allPois via compareDeliveriesData$;
      // don't clobber it with the single-delivery query results.
      if (this.selectedCompareDelivery) {
        return
      }

      this._loadPoisData(rows)
    })

    effect(() => {
      const aggs = this.aggregationsQuery.data()
      if (!aggs || this.selectedPOI) return

      // Skip when compare aggregations will handle the stats
      if (this.selectedCompareDelivery) return

      const aggKey = this.hasCampaignCreation ? 'sum' : 'avg'
      Promise.resolve().then(() => {
        this.mobilityStats = {
          ...this.mobilityStats,
          globalAggs: aggs,
          visitors: aggs[aggKey],
        }
        this.changes.detectChanges()
      })
    })

    // Compare aggregations effect — proper server-side stats for delivery comparison
    effect(() => {
      const rows = this.compareAggregationsQuery.data()
      if (!rows?.length || !this.selectedCompareDelivery || this.selectedPOI)
        return

      const refId = untracked(() => this.aggregationsFilters().delivery_id)
      const aggKey = this.hasCampaignCreation ? 'sum' : 'avg'
      const stats = OohStatsHelper.buildCompareDeliveryStats(
        rows,
        refId,
        aggKey,
      )
      if (!stats) return

      Promise.resolve().then(() => {
        this.mobilityStats = {
          ...this.mobilityStats,
          ...stats,
        }
        this.changes.detectChanges()
      })
    })

    effect(() => {
      const detail = this.poiInfoQuery.data()
      // Read compare signals so the effect re-fires when comparison changes
      const rawCompDetail = this.comparePoiInfoQuery.data()
      const comparePoiActive = !!this.comparePoiInfoFilters().poi_id
      const compareDeliveryActive = this.hasDeliveryCompare()
      if (!detail || !this.selectedPOI) return

      // When delivery compare is active, _processGridStatsByPoi handles stats
      if (compareDeliveryActive) return

      // Discard stale cached data when POI compare is no longer active
      const compDetail = comparePoiActive ? rawCompDetail : null
      const isComparing = compDetail != null

      const nextStats = {
        ...this.mobilityStats,
        globalAggs: isComparing
          ? [
              OohStatsHelper.buildDetailAggs(detail),
              OohStatsHelper.buildDetailAggs(compDetail),
            ]
          : OohStatsHelper.buildDetailAggs(detail),
        visitors: isComparing
          ? [detail.nvisitors, compDetail.nvisitors]
          : detail.nvisitors,
        poiTraffic: isComparing
          ? [{ sum: detail.traffic }, { sum: compDetail.traffic }]
          : { sum: detail.traffic },
        globalTraffic: { avg: detail.avgtraffic },
        targetStats: {
          ...(this.mobilityStats?.targetStats || {}),
          ...OohStatsHelper.buildTargetStatsFromQuery(
            detail,
            compDetail,
            isComparing,
          ),
        },
      }

      const promises = [this._fetchAggregatedStats(this.selectedPOI)]
      if (isComparing) {
        promises.push(
          this._fetchAggregatedStats(this.comparePoiInfoFilters().poi_id),
        )
      }

      Promise.resolve().then(() => {
        this.mobilityStats = nextStats

        if (this.mapLoaded) {
          this.paintPolygonsLayer()
        }

        if (!this.mobilityStats?.aggregatedStats) {
          this._aggregatedStatsLoading = true
        }
        this.changes.detectChanges()
      })

      Promise.all(promises)
        .then((results) => {
          this.mobilityStats = {
            ...this.mobilityStats,
            aggregatedStats: OohStatsHelper.mergeAggregatedStats(results),
          }
          this._aggregatedStatsLoading = false
          this.changes.detectChanges()
        })
        .catch((error) => {
          this._aggregatedStatsLoading = false
          this.changes.detectChanges()
          console.error('Error fetching aggregated stats:', error)
        })
    })
  }

  ngOnInit() {
    this.context.keyIndicatorLabels = {
      nvisitors: 'Reach',
      nvisits: 'Impressions',
    }
    this.context.keyIndicator = { value: 'nvisitors', label: 'Reach' }

    super.ngOnInit()
    this.context.setupKeyIndicators()

    this.subscriptions.add(
      this.context.deliveriesChanges.subscribe((ev) => {
        const delivery = ev?.delivery
        if (delivery) {
          const columns = this.getColumnsFromTarget()
          this.poisFilters.set({
            ...this.poisFilters(),
            delivery_id: delivery.id,
            delivery_start_date: delivery.start,
            columns,
          })
          this.streetsFilters.set({
            ...this.streetsFilters(),
            delivery_id: delivery.id,
          })
          this.aggregationsFilters.set({
            ...this.aggregationsFilters(),
            delivery_id: delivery.id,
            delivery_start_date: delivery.start,
            columns,
          })
          this.poiInfoFilters.set({
            ...this.poiInfoFilters(),
            delivery_id: delivery.id,
            delivery_start_date: delivery.start,
          })
          this.comparePoiInfoFilters.set({
            ...this.comparePoiInfoFilters(),
            delivery_id: delivery.id,
            delivery_start_date: delivery.start,
          })
          this.compareDeliveryFilters.set({
            ...this.compareDeliveryFilters(),
            delivery_id_1: delivery.id,
            delivery_start_date_1: delivery.start,
          })
          this.compareDeliveriesFilters.set({
            ...this.compareDeliveriesFilters(),
            delivery_id_1: delivery.id,
            delivery_start_date_1: delivery.start,
          })
          this.compareAggregationsFilters.set({
            ...this.compareAggregationsFilters(),
            delivery_id_1: delivery.id,
            delivery_start_date_1: delivery.start,
            columns,
          })
        }
      }),
    )
    this.subscriptions.add(
      this.context.filterChanges.subscribe(() => {
        const day = this.context.filters['day']?.value || 'All'
        const hour = this.context.filters['hour']?.value || 'All'

        const filter_combined = `${day}-${hour}`
        const columns = this.getColumnsFromTarget()

        this.poisFilters.set({
          ...this.poisFilters(),
          filter_combined,
          columns,
        })

        this.streetsFilters.set({
          ...this.streetsFilters(),
          filter_combined,
        })

        this.aggregationsFilters.set({
          ...this.aggregationsFilters(),
          filter_combined,
          columns,
        })
        this.poiInfoFilters.set({
          ...this.poiInfoFilters(),
          filter_combined,
        })
        this.comparePoiInfoFilters.set({
          ...this.comparePoiInfoFilters(),
          filter_combined,
        })
        this.compareDeliveryFilters.set({
          ...this.compareDeliveryFilters(),
          filter_combined,
        })
        this.compareDeliveriesFilters.set({
          ...this.compareDeliveriesFilters(),
          filter_combined,
        })
        this.compareAggregationsFilters.set({
          ...this.compareAggregationsFilters(),
          filter_combined,
          columns,
        })
      }),
    )

    this.subscriptions.add(
      this.context.$currentTags.subscribe((tagGroups) => {
        this.poisFilters.set({
          ...this.poisFilters(),
          tag_groups: tagGroups,
        })
        this.compareDeliveriesFilters.set({
          ...this.compareDeliveriesFilters(),
          tag_groups: tagGroups,
        })
        if (this.mapLoaded) {
          this.doComponentChanges()
        } else {
          this.changes.markForCheck()
        }
      }),
    )
    this.subscriptions.add(
      this.context.$minMaxFilter.subscribe((reach) => {
        this.poisFilters.set({
          ...this.poisFilters(),
          reach: reach ?? [],
        })
        if (this.mapLoaded) {
          this.doComponentChanges()
        } else {
          this.changes.markForCheck()
        }
      }),
    )

    this.subscriptions.add(
      this.context.poiChanges.subscribe((p) => {
        const poiId = p?.value || p || ''
        if (this.mobilityStats) {
          this.mobilityStats = { ...this.mobilityStats, aggregatedStats: null }
        }
        this.poiInfoFilters.set({
          ...this.poiInfoFilters(),
          poi_id: poiId,
        })
        this.compareDeliveryFilters.set({
          ...this.compareDeliveryFilters(),
          poi_id: poiId,
        })
        if (p?.centroid) {
          this.flyToPOI(p.centroid)
        } else if (p) {
          this.flyToPOI()
        } else {
          this.flyToBbox()
        }
      }),
    )

    this.poiLayerProperties['highlight_source'] = { type: 'number' }

    this.subscriptions.add(
      combineLatest([this.poiData$, this.comparePoiData$]).subscribe(
        ([ref, comp]) => {
          if (!ref || !comp || !this.selectedComparePOI) return

          setTimeout(() => {
            if (!this.selectedComparePOI) return
            this.mobilityStats = {
              ...this.mobilityStats,
              ...OohStatsHelper.buildCompareStats(
                ref,
                comp,
                this.mobilityStats?.targetStats,
                true,
              ),
            }
            this.changes.detectChanges()
          })
        },
      ),
    )

    this.subscriptions.add(
      this.compareDeliveryData$.subscribe((data) => {
        if (!data || !this.selectedCompareDelivery) return
        const [ref, comp] = data
        if (!ref || !comp) return

        setTimeout(() => {
          if (!this.selectedCompareDelivery) return
          this.mobilityStats = {
            ...this.mobilityStats,
            ...OohStatsHelper.buildCompareStats(
              ref,
              comp,
              this.mobilityStats?.targetStats,
              this.selectedComparePOI != null,
            ),
          }
          this.changes.detectChanges()

          if (this.selectedPOI && !this.selectedComparePOI) {
            this._aggregatedStatsLoading = true
            const poiId = this.selectedPOI
            const compareDelivery = this.selectedCompareDelivery
            Promise.all([
              this._fetchAggregatedStats(poiId),
              this._fetchAggregatedStats(poiId, compareDelivery),
            ])
              .then((results) => {
                // Bail if the user has since changed POI or compare delivery
                if (
                  this.selectedPOI !== poiId ||
                  this.selectedCompareDelivery !== compareDelivery ||
                  this.selectedComparePOI
                ) {
                  return
                }
                this.mobilityStats = {
                  ...this.mobilityStats,
                  aggregatedStats: OohStatsHelper.mergeAggregatedStats(results),
                }
              })
              .catch((error) => {
                console.error('Error fetching aggregated stats:', error)
              })
              .finally(() => {
                this._aggregatedStatsLoading = false
                this.changes.detectChanges()
              })
          }
        })
      }),
    )

    // Compare two deliveries (no POI selected) — update map POIs
    this.subscriptions.add(
      this.compareDeliveriesData$.subscribe((rows) => {
        if (!rows || !this.selectedCompareDelivery || this.selectedPOI) return

        setTimeout(() => {
          if (!this.selectedCompareDelivery || this.selectedPOI) return

          // Update POI list with compare data for map display
          this.allPois = OohStatsHelper.parsePoiRows(rows)
          this.poiList = OohStatsHelper.buildPoiListItems(
            this.allPois,
            this.brand,
          )
          this._processPoiList()

          this.changes.detectChanges()
        })
      }),
    )

    // this.flyToBbox()
  }

  protected flyToBbox() {
    if (!this.map || !this.mapBbox?.length) return
    this.map.flyToBbox(this.mapBbox, { padding: 150 })
  }

  ngOnDestroy() {
    this.context.keyIndicatorLabels = {
      nvisitors: 'Visitors',
      nvisits: 'Visits',
    }
    Object.keys(this.context.filters).forEach((key) => {
      if (key !== 'target') {
        this.context.filters[key].value = this.context.filters[key].default
      }
    })
    this.context.$currentTags.next([])
    this.context.$minMaxFilter.next([])
    this.context.setPoi(null)
    super.ngOnDestroy()
  }

  removeBaseTrafficLayer() {
    if (this.map.map) {
      if (this.map.map.getLayer(this.trafficBaseId)) {
        this.map.map.removeLayer(this.trafficBaseId)
      }
      if (this.map.map.getSource(this.trafficBaseId)) {
        this.map.map.removeSource(this.trafficBaseId)
      }
    }
  }

  removeDataTrafficLayer() {
    this.map.map.removeLayer(this.trafficDataId)
    this.map.map.removeSource(this.trafficDataId)
  }

  removePolygonLayer() {
    if (this.map.map.getLayer(this.poiLayerId)) {
      this.map.map.removeLayer(this.poiLayerId)
    }
    if (this.map.map.getSource(this.poiLayerId)) {
      this.map.map.removeSource(this.poiLayerId)
    }
  }

  async paintDataTrafficLayer() {
    const stops =
      this.trafficQuantilesQuery.data() ??
      (await firstValueFrom(this.trafficQuantilesData$.pipe(filter(Boolean))))

    const geojson =
      this.streetsQuery.data() ??
      (await firstValueFrom(this.streetsData$.pipe(filter(Boolean))))

    if (!geojson) {
      return
    }

    const source = this.map.map.getSource(this.trafficDataId)

    if (source) {
      ;(source as any).setData(geojson)
    } else {
      this.map.map.addSource(this.trafficDataId, {
        type: 'geojson',
        data: geojson,
      })

      let before = ''
      if (this.clusterLayer) {
        const clusterLayerId = this.clusterLayer.layers[0].id
        if (this.map.map.getLayer(clusterLayerId)) {
          before = clusterLayerId
        }
      }

      this.map.map.addLayer(
        {
          id: this.trafficDataId,
          type: 'line',
          source: this.trafficDataId,
          paint: {
            'line-width': 2,
            'line-opacity': 1,
            'line-color': {
              property: 'nvisitors',
              stops,
            },
          },
        },
        before,
      )
    }

    // NOTE: Bring cluster and point layers to front
    this.map.moveLayer(this.clusterPointsId, '')
    this.map.moveLayer(this.clusterLabelsId, '')
    this.map.moveLayer(this.generalLayerId, '')
  }

  protected async initLayers(cartoLayer) {
    await OohMapHelper.loadMapIcons(this.map.map, this.brand, this.http)

    this.clusterLayer = new OOHClusterLayer({
      onClick: (ev) => this.onMapClick(ev),
      onMouseEnter: (ev) => this.createHoverPopup(ev),
      onMouseOut: () => this.hideHoverPopup(),
    })

    this.clusterLayer.init(this.map.map)

    // Compare-delivery mode owns allPois via compareDeliveriesData$; repopulate
    // the freshly created cluster from it instead of the single-delivery query.
    if (this.selectedCompareDelivery) {
      this._processPoiList()
      return
    }

    const rows =
      this.poisQuery.data() ??
      (await firstValueFrom(this.poisData$.pipe(filter(Boolean))))

    if (rows?.length) {
      this._loadPoisData(rows)
    }
  }

  private _initingLayers = false

  async _processMap() {
    // After basemap change the cluster layer was destroyed — recreate it
    if (!this.clusterLayer && !this._initingLayers) {
      this._initingLayers = true
      try {
        await this.initLayers(null)
      } finally {
        this._initingLayers = false
      }
    }

    const promises = []

    if (this.hasTrafficData) {
      promises.push(this.paintDataTrafficLayer())
    }

    if (this.selectedPOI) {
      promises.push(this.paintPolygonsLayer())
    } else {
      this.removePolygonLayer()
    }

    return Promise.all(promises).catch((error) => {
      console.error('Error processing map:', error)
    })
  }

  getPolygonColors() {
    let colors = this.poiLegendColors // .map(c => `opacity(${c}, 0.8)`);
    if (this.isLightBasemap()) {
      colors = colors.slice().reverse()
    }

    const values = this.allPois.map((p) => p[this.keyIndicator])
    const minValue = Math.min(...values)
    const maxValue = Math.max(...values)

    return {
      property: this.keyIndicator,
      stops: [
        [minValue, '#F00'],
        [maxValue, '#00F'],
      ],
    }
  }

  paintPolygonsLayer() {
    const detail = this.poiDetail
    if (!detail?.geom || !this.map?.map) {
      return
    }

    const features: any[] = [
      {
        type: 'Feature',
        properties: { role: 'primary' },
        geometry: detail.geom,
      },
    ]

    const compDetail = this.comparePoiDetail
    if (compDetail?.geom) {
      features.push({
        type: 'Feature',
        properties: { role: 'compare' },
        geometry: compDetail.geom,
      })
    }

    const geojson = { type: 'FeatureCollection', features }

    const source = this.map.map.getSource(this.poiLayerId)
    if (source) {
      ;(source as any).setData(geojson)
      return
    }

    this.map.map.addSource(this.poiLayerId, {
      type: 'geojson',
      data: geojson,
    })

    let before = ''
    if (this.clusterLayer) {
      const clusterLayerId = this.clusterLayer.layers[2].id
      if (this.map.map.getLayer(clusterLayerId)) {
        before = clusterLayerId
      }
    }

    this.map.map.addLayer(
      {
        id: this.poiLayerId,
        type: 'fill',
        source: this.poiLayerId,
        paint: {
          'fill-opacity': 0.4,
          'fill-color': 'blue',
        },
      },
      before,
    )
  }

  setupLayerLegendUpdate(layerId, dueTime) {}

  changeBasemap(item) {
    // Layers may not exist yet on first load; safe to ignore cleanup errors
    try {
      this.cleanCartoLayers()
    } catch (e) {}

    this.currentBasemap = item
    // Don't call initMap() — onMapLoaded() fires after style.load,
    // triggering doComponentChanges() → _processMap() which re-creates the cluster layer
  }

  cleanCartoLayers() {
    if (this.hasTrafficData) {
      this.removeBaseTrafficLayer()
      this.removeDataTrafficLayer()
    }
    this.removePolygonLayer()
    this.clusterLayer.destroy()
    delete this.clusterLayer
  }

  hasTargetData(targetKey) {
    const target =
      this.mobilityStats.targetStats &&
      this.mobilityStats.targetStats[targetKey]
    return (
      target &&
      target.some((el) =>
        Array.isArray(el) ? el.some((el2) => el2.value) : el.value,
      )
    )
  }

  updateClusterLayerStyle() {
    const style = OohMapHelper.getPointsStyle({
      allPoisInCampaign: this.allPoisInCampaign,
      campaignPOIIds: this.campaignPOIIds,
      selectedPOI: this.selectedPOI,
      selectedComparePOI: this.selectedComparePOI,
    })
    this.clusterLayer.setPointsStyle(style)
    const pointsFilter = OohMapHelper.getPointsFilter({
      selectedPOI: this.selectedPOI,
      selectedComparePOI: this.selectedComparePOI,
      hasAllPOIsInPOIMap: this.hasAllPOIsInPOIMap,
    })
    this.clusterLayer.setPointsFilter(pointsFilter)
  }

  getVariableFromFeature(feature, key) {
    if (!feature) {
      return null
    }

    const keyReplacements = {
      count: 'point_count',
      ntrips: this.keyIndicator,
      ntrips_other: `${this.keyIndicator}_other`,
    }

    if (keyReplacements[key]) {
      key = keyReplacements[key]
    }

    const value = feature.properties && feature.properties[key]

    if (key === 'ntrips_avg') {
      const count = feature.properties.point_count
      const sum = feature.properties[this.keyIndicator]
      return sum / count
    }

    if (value === 'null') {
      return null
    }

    return value
  }

  // Fully overrides parent createHoverPopup (no super call) because:
  // - OOH uses Mapbox GL features (feat.properties) instead of CARTO VL (feat.variables)
  // - mousemove is unpatched from Zone.js, so detectChanges() is used instead of zone.run()
  createHoverPopup(ev) {
    const position = ev.position || ev.point
    if (!position) {
      return
    }

    const feat = ev.features?.[0]
    if (!feat) {
      return
    }

    const popupMargin = 12
    const id = this.getVariableFromFeature(feat, 'id') || feat.id
    const count = this.getVariableFromFeature(feat, 'count')
    const ntrips = this.getVariableFromFeature(feat, 'ntrips') || 0
    const ntrips_other = this.getVariableFromFeature(feat, 'ntrips_other') || 0
    const isCluster = Number(count) > 1
    const name = isCluster ? `${count} POIs` : String(id).split(':').pop()

    const nvars = [
      {
        value: ntrips,
        label: this.context.keyIndicator?.label || 'Reach',
        key: this.keyIndicator,
      },
    ]

    const tooltip = []
    const data: any = {
      id,
      name,
      ntrips,
      ntrips_other,
      nvars,
      keyIndicator: this.keyIndicator,
    }

    if (isCluster && !this.selectedPOI && !this.compareMode) {
      data.ntrips_avg = this.getVariableFromFeature(feat, 'ntrips_avg')
      tooltip.push({ value: 'ntrips_avg', label: 'Avg. Reach / POI' })
    } else if (this.selectedComparePOI && !ntrips_other) {
      const strId = String(id)
      const isPrimary = strId === String(this.selectedPOI)
      const isCompare = strId === String(this.selectedComparePOI)

      if (isPrimary) {
        const otherPOI = this.allPois.find(
          (p) => String(p.id) === String(this.selectedComparePOI),
        )
        if (otherPOI) {
          data.ntrips_other = otherPOI[this.keyIndicator]
        }
      } else if (isCompare) {
        const otherPOI = this.allPois.find(
          (p) => String(p.id) === String(this.selectedPOI),
        )
        if (otherPOI) {
          data.ntrips_other = otherPOI[this.keyIndicator]
        }
      }
    } else if (this.selectedCompareDelivery && !ntrips_other) {
      const strId = String(id)
      if (this.selectedPOI && strId === String(this.selectedPOI)) {
        const ref = this.poiDetail
        const comp = this.comparePoiDetail
        if (ref && comp) {
          data.ntrips = ref[this.keyIndicator]
          data.ntrips_other = comp[this.keyIndicator]
        }
      } else {
        const poi = this.allPois.find((p) => String(p.id) === strId)
        if (poi) {
          data.ntrips_other = poi[`${this.keyIndicator}_other`] || 0
        }
      }
    }

    this.hoverPopupData = {
      top: position.y + popupMargin,
      left: position.x + popupMargin,
      data,
      show: true,
      layerId: feat.layer?.id,
      primary: !this.selectedPOI,
      showFrom: false,
      tooltip: tooltip.length ? tooltip : this.currentDelivery?.tooltip || [],
      updated: false,
    }
    this.changes.detectChanges()
  }

  hideHoverPopup = () => {
    this.hoverPopupData.show = false
    this.hoverPopupData.layerId = ''
    this.changes.detectChanges()
  }

  flyToPOI(center?: [number, number]) {
    if (!center) {
      const poi = this.allPois.find((p) => p.id === this.selectedPOI)
      if (!poi) return
      center = [poi.lng, poi.lat]
    }
    this.map.flyTo({
      center,
      zoom: 16,
      speed: 0.5,
      essential: true,
    })
  }

  async onPOICompareSelected(event) {
    event = event || ''
    this.currentCompareValue = event.label || ''
    this.selectedComparePOI = event.value
    const current = this.poiInfoFilters()
    this.comparePoiInfoFilters.set({
      poi_id: event?.value || '',
      delivery_id: current.delivery_id,
      delivery_start_date: current.delivery_start_date,
      filter_combined: current.filter_combined,
    })

    // Clear delivery comparison when entering POI-to-POI comparison
    if (event.value && this.selectedCompareDelivery) {
      this.selectedCompareDelivery = null
      this.compareDeliveriesFilters.set({
        ...this.compareDeliveriesFilters(),
        delivery_id_2: 0,
        delivery_start_date_2: '',
      })
      this.compareAggregationsFilters.set({
        ...this.compareAggregationsFilters(),
        delivery_id_2: 0,
        delivery_start_date_2: '',
      })
      // Restore original POIs from single-delivery query
      const rows = this.poisQuery.data()
      if (rows?.length) {
        this._loadPoisData(rows)
      }
    }

    if (this.mapLoaded) {
      if (this.selectedComparePOI) {
        const primaryPoi = this.poiList.find(
          (p) => p.value === this.selectedPOI,
        )
        const comparePoi = this.poiList.find(
          (p) => p.value === this.selectedComparePOI,
        )
        if (primaryPoi?.centroid && comparePoi?.centroid) {
          const coords = [primaryPoi.centroid, comparePoi.centroid]
          const minX = Math.min(...coords.map((c) => c[0]))
          const maxX = Math.max(...coords.map((c) => c[0]))
          const minY = Math.min(...coords.map((c) => c[1]))
          const maxY = Math.max(...coords.map((c) => c[1]))
          this.map.flyToBbox([minX, minY, maxX, maxY], {
            padding: 100,
            maxZoom: 16,
            speed: 0.5,
          })
        }
      }
      this.updateClusterLayerStyle()
      this.doComponentChanges()
    }
  }

  deliveryToCompareSelected(delivery) {
    super.deliveryToCompareSelected(delivery)

    if (delivery) {
      this.compareDeliveryFilters.set({
        ...this.compareDeliveryFilters(),
        delivery_id_2: delivery.id,
        delivery_start_date_2: delivery.start,
      })
      this.compareDeliveriesFilters.set({
        ...this.compareDeliveriesFilters(),
        delivery_id_2: delivery.id,
        delivery_start_date_2: delivery.start,
      })
      this.compareAggregationsFilters.set({
        ...this.compareAggregationsFilters(),
        delivery_id_2: delivery.id,
        delivery_start_date_2: delivery.start,
      })
    } else {
      this.compareDeliveryFilters.set({
        ...this.compareDeliveryFilters(),
        delivery_id_2: 0,
        delivery_start_date_2: '',
      })
      this.compareDeliveriesFilters.set({
        ...this.compareDeliveriesFilters(),
        delivery_id_2: 0,
        delivery_start_date_2: '',
      })
      this.compareAggregationsFilters.set({
        ...this.compareAggregationsFilters(),
        delivery_id_2: 0,
        delivery_start_date_2: '',
      })

      // Restore original POIs from single-delivery query
      const rows = this.poisQuery.data()
      if (rows?.length) {
        this._loadPoisData(rows)
      }
    }

    // Clear POI-to-POI comparison when entering delivery compare mode
    if (delivery && this.selectedComparePOI) {
      this.selectedComparePOI = null
      this.currentCompareValue = ''
      this.comparePoiInfoFilters.set({
        ...this.comparePoiInfoFilters(),
        poi_id: '',
      })
    }
  }

  onPOISelected(event) {
    event = event || ''
    this.currentSelectValue = event.label || event.value || ''
    this.context.setPoi(event || null)
    const wasComparing = this.selectedComparePOI != null
    this.selectedComparePOI =
      event.value === undefined ? null : this.selectedComparePOI
    if (event.value === undefined) {
      if (this.poiPopup) {
        this.poiPopup.remove()
      }
      if (wasComparing) {
        this.comparePoiInfoFilters.set({
          ...this.comparePoiInfoFilters(),
          poi_id: '',
        })
      }
      // Re-apply compare deliveries data so hover popups show comparison
      if (this.selectedCompareDelivery) {
        const rows = this.compareDeliveriesQuery.data()
        if (rows?.length) {
          this.allPois = OohStatsHelper.parsePoiRows(rows)
          this.poiList = OohStatsHelper.buildPoiListItems(
            this.allPois,
            this.brand,
          )
          this._processPoiList()
        }
      }
    }

    this.filteredPoiList = this.currentSelectValue
      ? this.poiList.filter((poi) => poi.label !== this.currentSelectValue)
      : this.poiList

    if (this.mapLoaded) {
      if (this.clusterLayer) {
        this.clusterLayer.updateClusterMaxZoom(!!this.selectedPOI)
      }
      this.updateClusterLayerStyle()
    }
  }

  selectPOIForCampaign(feature) {
    const id = String(this.getVariableFromFeature(feature, 'id') || feature.id)
    const selected = this.campaignPOIIds.some((d) => d === id)
    this.zone.run(() => {
      const base = this.campaignPOIIds.filter((p) => p !== 'all')
      if (selected) {
        this.campaignPOIIds = base.filter((d) => d !== id)
      } else {
        this.campaignPOIIds = base.concat(id)
      }
    })

    this.updateClusterLayerStyle()
  }

  onMapClick(ev) {
    const feature = ev.features[0]
    if (!feature) {
      return
    }

    const isCluster = this.getVariableFromFeature(feature, 'count') > 1
    if (isCluster) {
      return
    }

    if (this.campaignCreationActive) {
      ev.features.forEach((f) => this.selectPOIForCampaign(f))
      return
    }

    // When delivery comparison is active, select POI to show comparison in sidebar
    if (this.selectedCompareDelivery) {
      const poi = this.getPOIFromFeature(feature)
      this.onPOISelected(poi)
      return
    }

    this.isActivedCompareSelector = false
    const poi = this.getPOIFromFeature(feature)
    const compareSelectCheck = this.hasCampaignCreation
      ? this.isCompareSelectPending || this.selectedPOI
      : this.isCompareSelectPending

    if (compareSelectCheck) {
      this.onPOICompareSelected(poi)
    } else {
      this.onPOISelected(poi)
    }
  }

  getPOIFromFeature(feature) {
    const name = this.getVariableFromFeature(feature, 'name')
    const id = this.getVariableFromFeature(feature, 'id') || feature.id
    return {
      value: id,
      region_id: this.getVariableFromFeature(feature, 'region_id'),
      centroid: feature.geometry.coordinates,
      label: this.isMapGridMode() ? `Grid #${feature.id}` : name,
    }
  }

  setupPoiPopup(ev) {}

  canShowSelectFilter() {
    return !!this.selectedPOI || (!this.isLoading && !this.isMapGridMode())
  }

  setupBrandIcon() {
    this._brand = 'asset'
  }

  getVisitorsAgg() {
    const aggs = ['sum', 'avg'].map((agg) => agg + this.keyIndicator)
    let promises = [
      this.mobilityService.getVisitorsMultipleAggs(
        this.selectedPOI,
        this.currentDelivery,
        aggs,
      ),
    ]
    if (this.selectedCompareDelivery) {
      promises = [
        this.mobilityService.getVisitorsMultipleAggs(
          this.selectedPOI,
          this.currentDelivery,
          aggs,
        ),
        this.mobilityService.getVisitorsMultipleAggs(
          this.selectedPOI,
          this.selectedCompareDelivery,
          aggs,
        ),
      ]
    }
    if (this.selectedComparePOI) {
      promises = [
        this.mobilityService.getVisitorsMultipleAggs(
          this.selectedPOI,
          this.currentDelivery,
          aggs,
        ),
        this.mobilityService.getVisitorsMultipleAggs(
          this.selectedComparePOI,
          this.currentDelivery,
          aggs,
        ),
      ]
    }

    const aggKey =
      (this.hasCampaignCreation ? 'sum' : 'avg') + this.keyIndicator
    return Promise.all(promises).then((v) => {
      const mapped = v.map((d) => d[aggKey])
      return mapped.length === 1 ? mapped[0] : mapped
    })
  }

  _processGridStatsByPoi() {
    const poi = this.selectedPOI || null
    return Promise.all([
      this.mobilityService.getTargetStats(poi, null, null, this.compareOptions), // targetStats
      this.getVisitorsAgg(), // totalVisitors
      // this.mobilityService.getVisitorsAgg(poi, this.compareOptions), // totalVisitors
      this.mobilityService.getTargetStats(
        poi,
        null,
        null,
        this.compareOptions,
        false,
        true,
      ), // targetStats disagregated by filter
      this.mobilityService.getVisitorsMultipleAggs(null, this.currentDelivery, [
        'min',
        'max',
        'count',
        'avg',
        'sum',
      ]), // globalAggs
      this.mobilityService.getTrafficAgg(null, this.currentDelivery, ['avg']), // globalAvgtraffic
      this.mobilityService.getTrafficAgg(
        poi,
        this.currentDelivery,
        ['sum'],
        this.compareOptions,
      ), // poiTraffic
    ])
      .then((values) => {
        const ref = this.poiDetail
        const comp = this.comparePoiDetail
        const isComparing =
          this.selectedComparePOI || this.selectedCompareDelivery
        const hasQueryData = ref && (!isComparing || comp)
        const isCompareMode = !!this.compareOptions

        this.mobilityStats = {
          poiTraffic: hasQueryData
            ? comp
              ? [{ sum: ref.traffic }, { sum: comp.traffic }]
              : { sum: ref.traffic }
            : values[5],
          globalTraffic: hasQueryData ? { avg: ref.avgtraffic } : values[4],
          globalAggs: hasQueryData
            ? comp
              ? [
                  OohStatsHelper.buildDetailAggs(ref),
                  OohStatsHelper.buildDetailAggs(comp),
                ]
              : OohStatsHelper.buildDetailAggs(ref)
            : values[3],
          targetStats: {
            ...OohStatsHelper.parseTargetStats(
              values[0],
              this.currentDelivery,
              isCompareMode,
            ),
            ...(ref
              ? OohStatsHelper.buildTargetStatsFromQuery(
                  ref,
                  comp,
                  this.selectedComparePOI != null,
                )
              : {}),
          },
          visitors: hasQueryData
            ? comp
              ? [ref.nvisitors, comp.nvisitors]
              : ref.nvisitors
            : values[1],
          aggregatedStats: {
            age: isCompareMode
              ? (values[2].age || []).map((val) =>
                  OohStatsHelper.formatLegacyAggregatedData(val.flat()),
                )
              : OohStatsHelper.formatLegacyAggregatedData(values[2].age),
            gender: isCompareMode
              ? (values[2].gender || []).map((val) =>
                  OohStatsHelper.formatLegacyAggregatedData(val.flat()),
                )
              : OohStatsHelper.formatLegacyAggregatedData(values[2].gender),
            habits: isCompareMode
              ? (values[2].gender || []).map((val) =>
                  OohStatsHelper.formatLegacyAggregatedData(
                    val.flat(),
                    'day',
                    'habits',
                  ),
                )
              : OohStatsHelper.formatLegacyAggregatedData(
                  values[2].gender,
                  'day',
                  'habits',
                ),
          },
        }
        this.mobilityStats.targetStats =
          OohStatsHelper.postProcessInterestedStats(
            this.mobilityStats.targetStats,
            isCompareMode,
          )

        if (ref) {
          if (this.selectedCompareDelivery && !this.selectedComparePOI) {
            return
          }
          const aggPromises = [this._fetchAggregatedStats(this.selectedPOI)]
          if (this.selectedComparePOI && comp) {
            aggPromises.push(
              this._fetchAggregatedStats(this.selectedComparePOI),
            )
          }
          Promise.all(aggPromises).then((results) => {
            this.mobilityStats = {
              ...this.mobilityStats,
              aggregatedStats: OohStatsHelper.mergeAggregatedStats(results),
            }
            this.changes.detectChanges()
          })
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }

  _processGeneralStats() {
    const aggs = this.aggregationsQuery.data()
    if (aggs && !this.selectedPOI && !this.selectedCompareDelivery) {
      const aggKey = this.hasCampaignCreation ? 'sum' : 'avg'
      this.mobilityStats = {
        ...this.mobilityStats,
        globalAggs: aggs,
        visitors: aggs[aggKey],
      }
    }

    // Re-apply cached compare aggregation stats after _resetStats() wipe
    if (this.selectedCompareDelivery && !this.selectedPOI) {
      const rows = this.compareAggregationsQuery.data()
      if (rows?.length) {
        const refId = this.aggregationsFilters().delivery_id
        const aggKey = this.hasCampaignCreation ? 'sum' : 'avg'
        const stats = OohStatsHelper.buildCompareDeliveryStats(
          rows,
          refId,
          aggKey,
        )
        if (stats) {
          this.mobilityStats = {
            ...this.mobilityStats,
            ...stats,
          }
        }
      }
    }

    return Promise.resolve()
  }

  // OOH manages POIs via TanStack Query (poisQuery effect), not getTerritoryList()
  _processPoi() {
    return Promise.resolve()
  }

  _loadPoisData(rows: PoiRow[]) {
    this.allPois = OohStatsHelper.parsePoiRows(rows)
    this.poiList = OohStatsHelper.buildPoiListItems(this.allPois, this.brand)

    for (const p of this.allPois) {
      for (const tag of p.tags) {
        this.tagLabels[tag.name] = tag.label
        this.tagIds[tag.name] = tag.id
      }
    }

    this._processPoiList()
    this.changes.detectChanges()
  }

  _processPoiList() {
    if (this.clusterLayer) {
      this.clusterLayer.loadData(this.allPois, !this.selectedPOI)
      this.updateClusterLayerStyle()
    }
  }

  _processStats(): Promise<any> {
    this.showStats = !!this.selectedPOI
    if (this.selectedPOI) {
      return super._processStats().catch((error) => {
        console.error('Error processing stats:', error)
      })
    } else {
      return this._processGeneralStats()
    }
  }

  processLegendLabels(viz) {
    if (this.hasTrafficData) {
      this.mobilityStats.legend = ['Low', 'High']
      return
    }
    return super.processLegendLabels(viz)
  }

  getFilterPlaceHolder() {
    const placeholder = this.translateService.instant('Search for a store in')
    return `${placeholder} ${
      !environment.politis ? this.context.city.name : ''
    }`
  }

  getLegendColors() {
    if (this.hasTrafficData) {
      return [
        'gold',
        '#fde0c5',
        '#facba6',
        '#f8b58b',
        '#f59e72',
        '#f2855d',
        '#ef6a4c',
        '#eb4a40',
      ]
    }
    return super.getLegendColors()
  }

  onTagsChange($event) {
    this.context.$currentTags.next($event)
  }

  onMinMaxChange($event) {
    this.context.$minMaxFilter.next($event)
  }

  onFloatWidgetCollapsed($event) {
    Promise.resolve().then(() => {
      this.floatBarCollapsed = $event
      this.changes.detectChanges()
    })
  }

  activateCampaignCreation(flag) {
    this.campaignCreationActive = flag
  }

  confirmCampaign() {
    this.showCampaignCreationModal = true
  }

  clearCampaign() {
    this.campaignPOIIds = []
    this.updateClusterLayerStyle()
  }

  editCampaignPOIs(pois) {
    this.campaignPOIIds = pois
    this.updateClusterLayerStyle()
  }

  onCampaignSaved() {
    this.campaignCreationActive = false
    this.showCampaignCreationModal = false
    this.campaignPOIIds = []
    this.updateClusterLayerStyle()
  }

  createDeliveryLabel(delivery: { start: string; finish: string }) {
    return OohStatsHelper.createDeliveryLabel(delivery)
  }

  private getColumnsFromTarget(): string[] {
    return OohStatsHelper.getColumnsFromTargetFilter(
      this.context.filters['target'],
    )
  }
}
